import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayer, useAuth } from '../contexts/AppContext';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, 
  Shuffle, Repeat, Repeat1, Maximize2, Minimize2, X, PictureInPicture2, ChevronDown, Flag, Heart, MessageCircle, ListMusic, Share2, Link as LinkIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ReportModal } from './ReportModal';
import { CommentModal } from './CommentSection';
import { AddToPlaylistModal } from './AddToPlaylistModal';
import { resolvePlaybackUrl, isVideoProcessing, useHlsVideo, resolveThumbnailUrl } from '../utils/hlsPlayer';
import { AirPlayButton } from './AirPlayButton';
import { useAirPlay } from '../utils/airplay';
import { shareVideo, copyShareLink } from '../utils/share';
import { useLikeAuthGate } from './AuthPromptModal';

const API = process.env.REACT_APP_BACKEND_URL;

const MOCK_THUMBNAILS = [
  "https://images.unsplash.com/photo-1762279801044-3c37872f4534?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
];


// Tiny wrapper component so we can call the useHlsVideo hook conditionally
// (React rule: hooks can't sit behind an `if`). Mounted unconditionally
// inside MiniPlayer; passing videoUrl=null effectively no-ops.
const MiniPlayerHlsAttacher = ({ videoRef, videoUrl }) => {
  useHlsVideo(videoRef, videoUrl, { autoStart: false });
  return null;
};


// Fullscreen Player Component with auto-hide controls and mobile-first design
const FullscreenPlayer = ({
  videoRef,
  videoUrl,
  currentVideo,
  isPlaying,
  isMuted,
  isPiP,
  progress,
  duration,
  volume,
  shuffle,
  repeat,
  getThumbnail,
  formatTime,
  handleTimeUpdate,
  handleLoadedMetadata,
  handleEnded,
  handleSeek,
  handleVolumeChange,
  togglePlay,
  toggleMute,
  togglePiP,
  toggleShuffle,
  toggleRepeat,
  playNext,
  playPrevious,
  setIsPlaying,
  setIsFullscreen,
  onReport,
  onArtistClick,
  onLike,
  onPlaylist,
  onComment,
  isLiked: initialLiked
}) => {
  const { user } = useAuth();
  const requireAuthForLike = useLikeAuthGate();
  const [showControls, setShowControls] = useState(true);
  // Attach Bunny HLS (m3u8) → hls.js / native; legacy MP4 URLs fall through.
  useHlsVideo(videoRef, videoUrl, { autoStart: true });
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [liked, setLiked] = useState(initialLiked || false);
  const controlsTimeoutRef = useRef(null);

  // Auto-hide controls after 1.5s of inactivity (only while the video is
  // actually playing). We read the live `<video>` element state instead of
  // relying on the React `isPlaying` prop, because that prop comes from the
  // player context and can lag behind the actual <video> by hundreds of ms
  // on iOS Safari — which would leave the controls visible permanently
  // when the user reported it.
  const armHideTimer = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      const v = videoRef.current;
      // Only hide if the video is actively playing (not paused, not ended).
      if (v && !v.paused && !v.ended) {
        setShowControls(false);
      } else {
        // Still paused/buffering — re-arm so we hide as soon as it plays.
        armHideTimer();
      }
    }, 1500);
  }, [videoRef]);

  useEffect(() => {
    if (showControls) armHideTimer();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [showControls, isPlaying, armHideTimer]);

  // Also re-arm the hide timer when the underlying <video> element fires
  // its native `play` event — covers autoplay-on-mount where `isPlaying`
  // updates asynchronously.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => {
      setShowControls(true);
      armHideTimer();
    };
    v.addEventListener('play', onPlay);
    return () => v.removeEventListener('play', onPlay);
  }, [armHideTimer, videoRef]);

  const handleInteraction = () => {
    setShowControls(true);
    armHideTimer();
  };

  const handleVideoClick = () => {
    handleInteraction();
    togglePlay();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onMouseMove={handleInteraction}
      onTouchStart={handleInteraction}
      data-testid="fullscreen-player"
    >
      {/* Video Element - Full viewport. NOTE: src is attached by useHlsVideo
          (above) so hls.js can MSE-stream HLS or set src for native paths.
          Setting `src` here would race with hls.js and cause double-loads.
          `x-webkit-airplay="allow"` opts the element into Safari's AirPlay
          picker; harmless on every other browser. */}
      <video
        ref={videoRef}
        poster={getThumbnail(currentVideo)}
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onClick={handleVideoClick}
        playsInline
        autoPlay
        x-webkit-airplay="allow"
      />
      
      {/* Controls Overlay */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex flex-col"
          >
            {/* Top bar - minimize, report and PiP */}
            <div
              className="flex items-center justify-between px-4 pb-4 bg-gradient-to-b from-black/70 to-transparent"
              style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
            >
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsFullscreen(false)} 
                className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                data-testid="minimize-fullscreen-btn"
              >
                <ChevronDown className="w-6 h-6" />
              </Button>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Bail BEFORE flipping state — phantom-like-prevention.
                    if (!requireAuthForLike(user, currentVideo?.id)) return;
                    const newLiked = !liked;
                    setLiked(newLiked);
                    if (onLike) onLike(currentVideo?.id, newLiked);
                  }}
                  className={`rounded-full w-10 h-10 ${liked ? 'text-red-400 hover:bg-red-500/20' : 'text-white hover:bg-white/20'}`}
                  data-testid="like-btn-fullscreen"
                  title={liked ? "Unlike" : "Like"}
                >
                  <Heart className={`w-5 h-5 ${liked ? 'fill-red-400' : ''}`} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentVideo) shareVideo(currentVideo);
                  }}
                  className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  data-testid="share-btn-fullscreen"
                  title="Share"
                >
                  <Share2 className="w-5 h-5" />
                </Button>
                {/* Dedicated Copy-Link — desktop only. The Share button
                    above delegates to the OS share sheet on desktop
                    macOS Safari + Chrome, which has NO "Copy Link" entry
                    on macOS. This button bypasses the share sheet and
                    drops the same shareable URL onto the clipboard in
                    one click. `hidden md:inline-flex` keeps it off
                    mobile where the native share sheet already has a
                    working "Copy" option. See utils/share.js →
                    copyShareLink for the clipboard fallback chain. */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentVideo) copyShareLink(currentVideo);
                  }}
                  className="hidden md:inline-flex text-white hover:bg-white/20 rounded-full w-10 h-10"
                  data-testid="copy-link-btn-fullscreen"
                  title="Copy link"
                  aria-label="Copy link to video"
                >
                  <LinkIcon className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onComment) onComment(currentVideo?.id);
                  }}
                  className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  data-testid="comment-btn-fullscreen"
                  title="Comments"
                >
                  <MessageCircle className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPlaylist) onPlaylist(currentVideo?.id);
                  }}
                  className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  data-testid="playlist-btn-fullscreen"
                  title="Add to Playlist"
                >
                  <ListMusic className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={onReport} 
                  className="text-yellow-500 hover:bg-yellow-500/20 rounded-full w-10 h-10"
                  data-testid="report-btn-fullscreen"
                  title="Report video"
                >
                  <Flag className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={togglePiP} 
                  className={`text-white hover:bg-white/20 rounded-full w-10 h-10 ${isPiP ? 'bg-white/20' : ''}`}
                  data-testid="pip-btn-fullscreen"
                >
                  <PictureInPicture2 className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Center play/pause button (visible on tap) */}
            <div className="flex-1 flex items-center justify-center" onClick={handleVideoClick}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center"
              >
                {isPlaying ? (
                  <Pause className="w-10 h-10 text-white" />
                ) : (
                  <Play className="w-10 h-10 text-white fill-white ml-1" />
                )}
              </motion.div>
            </div>
            
            {/* Bottom controls */}
            <div
              className="bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pt-4 space-y-4"
              style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
            >
              {/* Video info */}
              <div className="flex items-center gap-3">
                <img 
                  src={getThumbnail(currentVideo)} 
                  alt={currentVideo.title} 
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0" 
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base truncate">{currentVideo.title}</h3>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onArtistClick) onArtistClick(currentVideo);
                      }}
                      className="text-zinc-400 text-sm truncate hover:text-white hover:underline transition-colors"
                      data-testid="fullscreen-artist-link"
                    >
                      {currentVideo.artist}
                    </button>
                    {currentVideo.creator_tier && (
                      <span 
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0"
                        style={{ background: `${currentVideo.creator_tier.color}20`, color: currentVideo.creator_tier.color }}
                      >
                        {currentVideo.creator_tier.badge}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <span className="text-white text-xs w-10 text-right font-mono">{formatTime(progress)}</span>
                <Slider 
                  value={[progress]} 
                  max={duration || 100} 
                  step={0.1} 
                  onValueChange={handleSeek} 
                  className="flex-1" 
                />
                <span className="text-white text-xs w-10 font-mono">{formatTime(duration)}</span>
              </div>

              {/* Playback controls */}
              <div className="flex items-center justify-between">
                {/* Left: Volume */}
                <div className="flex items-center gap-2 w-24">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => { toggleMute(); setShowVolumeSlider(!showVolumeSlider); }}
                    className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                  {showVolumeSlider && (
                    <Slider 
                      value={[volume]} 
                      max={1} 
                      step={0.01} 
                      onValueChange={handleVolumeChange} 
                      className="w-16" 
                    />
                  )}
                </div>

                {/* Center: Main controls */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={toggleShuffle} 
                    className={`rounded-full w-10 h-10 ${shuffle ? 'text-white bg-white/20' : 'text-zinc-400 hover:text-white hover:bg-white/20'}`}
                  >
                    <Shuffle className="w-5 h-5" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={playPrevious} 
                    className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  >
                    <SkipBack className="w-6 h-6" />
                  </Button>
                  <Button 
                    onClick={togglePlay} 
                    className="w-14 h-14 rounded-full bg-white text-black hover:bg-zinc-200 flex-shrink-0" 
                    data-testid="fullscreen-play-pause"
                  >
                    {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={playNext} 
                    className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  >
                    <SkipForward className="w-6 h-6" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={toggleRepeat} 
                    className={`rounded-full w-10 h-10 ${repeat !== 'none' ? 'text-white bg-white/20' : 'text-zinc-400 hover:text-white hover:bg-white/20'}`}
                  >
                    {repeat === 'one' ? <Repeat1 className="w-5 h-5" /> : <Repeat className="w-5 h-5" />}
                  </Button>
                </div>

                {/* Right: AirPlay (Safari-only, auto-hides elsewhere) +
                    fullscreen toggle. AirPlay is gated on `!is_lyric` so
                    the button only appears on full-song video cards, per
                    product spec. Width bumped from w-24 → w-28 so both
                    buttons fit without shifting the center transport
                    controls off-axis on narrow phones. */}
                <div className="flex items-center gap-2 w-28 justify-end">
                  {!currentVideo?.is_lyric && (
                    <AirPlayButton
                      videoRef={videoRef}
                      videoKey={currentVideo?.id}
                      data-testid="airplay-btn-fullscreen"
                    />
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setIsFullscreen(false)} 
                    className="text-white hover:bg-white/20 rounded-full w-10 h-10"
                  >
                    <Minimize2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always visible progress bar at bottom when controls hidden */}
      {!showControls && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <div 
            className="h-full bg-white transition-all duration-100"
            style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
          />
        </div>
      )}
    </motion.div>
  );
};

export const MiniPlayer = () => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  // AirPlay wireless state — kept in a ref so the event handlers (which
  // close over the initial `handleEnded`/`togglePlay`) always read the
  // latest value without re-registering. This is the iter-65 skip-loop
  // fix: when AirPlay hands off, iOS Safari briefly fires `ended` on the
  // local <video> as playback moves to the remote receiver. Without the
  // guard, `handleEnded` calls `playNext()` and the receiver ends up
  // skipping past every song. See utils/airplay.js for the underlying hook.
  const airplayWirelessRef = useRef(false);
  const airplayPlaybackStartTsRef = useRef(0);
  const airplay = useAirPlay(videoRef, null);
  useEffect(() => {
    airplayWirelessRef.current = airplay.wireless;
    // Stamp the moment AirPlay engaged so we can distinguish an early
    // spurious `ended` (right after handoff) from a genuine end-of-song
    // fired by the receiver much later.
    if (airplay.wireless) airplayPlaybackStartTsRef.current = Date.now();
  }, [airplay.wireless]);
  const [isMuted, setIsMuted] = useState(false);
  const [watchRecorded, setWatchRecorded] = useState(null);
  const [isPiP, setIsPiP] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isVideoLiked, setIsVideoLiked] = useState(false);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  // iter-99: comment icon on the fullscreen player was a no-op everywhere
  // except Pulse (which provided its own onComment). Now wired here so
  // comments work from Home, Discover, Library, /video/{id}, etc. — every
  // surface that opens the global player.
  const [showCommentModal, setShowCommentModal] = useState(false);
  const { token, user } = useAuth();
  const requireAuthForLike = useLikeAuthGate();

  const {
    currentVideo,
    queue,
    currentIndex,
    isPlaying,
    shuffle,
    repeat,
    volume,
    progress,
    duration,
    isFullscreen,
    isMiniPlayerVisible,
    playNext,
    playPrevious,
    togglePlay,
    toggleShuffle,
    toggleRepeat,
    closePlayer,
    setVolume,
    setProgress,
    setDuration,
    setIsPlaying,
    setIsFullscreen
  } = usePlayer();

  // Navigate to artist profile
  const handleArtistClick = (video) => {
    const artistId = video.user_id || video.artist_id || encodeURIComponent(video.artist || 'unknown');
    setIsFullscreen(false); // Close fullscreen player
    navigate(`/creator/${artistId}`);
  };

  // Track liked state when video changes
  useEffect(() => {
    setIsVideoLiked(currentVideo?.is_liked || false);
  }, [currentVideo?.id, currentVideo?.is_liked]);

  // Like/unlike handler for fullscreen player AND mini-player.
  // The auth gate runs HERE — both the fullscreen Heart and the mini-player
  // Heart funnel through this single function, so the shared modal fires
  // (and the heart does NOT flip optimistically) on logged-out users.
  const handleLikeVideo = async (videoId, isLiked) => {
    if (!videoId) return;
    if (!requireAuthForLike(user, videoId)) return;
    try {
      if (isLiked) {
        await axios.post(`${API}/api/favorites/${videoId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.delete(`${API}/api/favorites/${videoId}`, { headers: { Authorization: `Bearer ${token}` } });
      }
      setIsVideoLiked(isLiked);
    } catch (err) {
      console.debug('Like toggle failed:', err.message);
    }
  };

  useEffect(() => {
    if (currentVideo && currentVideo.id !== watchRecorded) {
      // Always record view (no auth needed)
      axios.post(`${API}/api/videos/${currentVideo.id}/view`).catch(() => { /* best-effort, non-blocking */ });
      // Record to watch history if authenticated
      if (token) recordWatch(currentVideo.id);
      setWatchRecorded(currentVideo.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per video; recordWatch is fire-and-forget
  }, [currentVideo, token]);

  // Watch-time tracking. AudIVue videos are SHORT (30-60s typical), so the
  // old 30-second interval would record ZERO for any view shorter than 30s
  // (a huge bug — most plays). We now:
  //   • tick every 5s while playing → record the actual elapsed delta
  //   • flush any remaining partial seconds when playback pauses / ends /
  //     the player unmounts (component cleanup)
  // Minimum granularity is 1 second per push to match the backend's `ge=1`
  // query-param guard.
  useEffect(() => {
    if (!currentVideo || !token || !isPlaying) return;
    let lastFlushAt = Date.now();
    const TICK_MS = 5_000;
    const interval = setInterval(() => {
      const now = Date.now();
      const seconds = Math.round((now - lastFlushAt) / 1000);
      if (seconds >= 1) {
        recordWatchTime(currentVideo.id, seconds);
        lastFlushAt = now;
      }
    }, TICK_MS);

    return () => {
      clearInterval(interval);
      // Flush whatever's accumulated since the last tick (covers
      // pause/ended/unmount/route-change). Without this, a 25s view ticks
      // 5× at 5s, then unmounts with 0s "left over" — but a quick 18s view
      // ticks 3× at 5s and would lose the trailing 3s on the old code.
      const remaining = Math.round((Date.now() - lastFlushAt) / 1000);
      if (remaining >= 1) {
        recordWatchTime(currentVideo.id, remaining);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-binding recordWatchTime would reset the tick interval
  }, [currentVideo, token, isPlaying]);

  const recordWatch = async (videoId) => {
    try {
      await axios.post(`${API}/api/watch-history/${videoId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      // Non-critical - don't interrupt playback for analytics failures
      console.debug('Watch history recording failed:', err.message);
    }
  };

  const recordWatchTime = async (videoId, seconds) => {
    try {
      await axios.post(`${API}/api/creator/watch-time/${videoId}?seconds=${seconds}`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      // Non-critical - don't interrupt playback for analytics failures
      console.debug('Watch time recording failed:', err.message);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play().catch(() => {
          // Autoplay may be blocked by browser - user interaction needed
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, currentVideo]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  // Listen for PiP exit
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handlePiPExit = () => setIsPiP(false);
    video.addEventListener('leavepictureinpicture', handlePiPExit);
    return () => video.removeEventListener('leavepictureinpicture', handlePiPExit);
  }, [currentVideo]);

  const handleTimeUpdate = () => {
    if (videoRef.current) setProgress(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleEnded = () => {
    // AirPlay handoff artifact: Safari fires `ended` on the LOCAL <video>
    // element the instant playback transfers to a wireless receiver. If we
    // acted on it we'd skip past the song the user just picked. Ignore any
    // `ended` that fires within 4 s of the handoff — plenty of buffer for
    // the receiver to take over, well below any realistic song length.
    // Actual end-of-song events from the receiver arrive minutes later and
    // fall through this guard cleanly.
    if (airplayWirelessRef.current) {
      const sinceHandoff = Date.now() - (airplayPlaybackStartTsRef.current || 0);
      if (sinceHandoff < 4000) return;
    }
    if (repeat === 'one') {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    } else {
      playNext();
    }
  };

  const handleSeek = (value) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value[0];
      setProgress(value[0]);
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    setIsMuted(value[0] === 0);
  };

  const toggleMute = () => {
    if (isMuted) { setVolume(1); setIsMuted(false); }
    else { setVolume(0); setIsMuted(true); }
  };

  const togglePiP = async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else {
        await video.requestPictureInPicture();
        setIsPiP(true);
      }
    } catch (err) {
      console.error('PiP not supported:', err);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getThumbnail = (video) => {
    // Priority: creator's custom > Bunny real thumbnail > legacy /api/files →
    // AUDIVUE branded placeholder (set in resolveThumbnailUrl default).
    return resolveThumbnailUrl(video);
  };

  const getVideoUrl = (video) => {
    // Prefer Bunny HLS (the new path); fall back to legacy /api/files for older videos.
    return resolvePlaybackUrl(video) || null;
  };

  if (!isMiniPlayerVisible || !currentVideo) return null;

  const videoUrl = getVideoUrl(currentVideo);

  return (
    <>
      {/* Mini-player HLS attach — only takes effect when the mini player's
          <video> is mounted (i.e. fullscreen is closed). Fullscreen has its
          own useHlsVideo call so each owner-component cleans up its own
          hls.js instance on unmount. */}
      <MiniPlayerHlsAttacher videoRef={videoRef} videoUrl={isFullscreen ? null : videoUrl} />
      {/* Fullscreen Player - Immersive Mode */}
      <AnimatePresence>
        {isFullscreen && (
          <FullscreenPlayer
            videoRef={videoRef}
            videoUrl={videoUrl}
            currentVideo={currentVideo}
            isPlaying={isPlaying}
            isMuted={isMuted}
            isPiP={isPiP}
            progress={progress}
            duration={duration}
            volume={volume}
            shuffle={shuffle}
            repeat={repeat}
            getThumbnail={getThumbnail}
            formatTime={formatTime}
            handleTimeUpdate={handleTimeUpdate}
            handleLoadedMetadata={handleLoadedMetadata}
            handleEnded={handleEnded}
            handleSeek={handleSeek}
            handleVolumeChange={handleVolumeChange}
            togglePlay={togglePlay}
            toggleMute={toggleMute}
            togglePiP={togglePiP}
            toggleShuffle={toggleShuffle}
            toggleRepeat={toggleRepeat}
            playNext={playNext}
            playPrevious={playPrevious}
            setIsPlaying={setIsPlaying}
            setIsFullscreen={setIsFullscreen}
            onReport={() => setShowReportModal(true)}
            onArtistClick={handleArtistClick}
            onLike={handleLikeVideo}
            isLiked={isVideoLiked}
            onPlaylist={(videoId) => setShowPlaylistModal(true)}
            onComment={() => setShowCommentModal(true)}
          />
        )}
      </AnimatePresence>

      {/* Comment Modal (iter-99) — wires the comment icon on the
          fullscreen player to the same modal Pulse already used. */}
      <CommentModal
        videoId={currentVideo?.id}
        isOpen={showCommentModal}
        onClose={() => setShowCommentModal(false)}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        video={currentVideo}
      />

      {/* Add to Playlist Modal */}
      <AddToPlaylistModal
        videoId={currentVideo?.id}
        isOpen={showPlaylistModal}
        onClose={() => setShowPlaylistModal(false)}
      />

      {/* Mini Player */}
      {!isFullscreen && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-[calc(2rem+56px)] md:bottom-0 left-0 md:left-64 right-0 z-50 glass-morphism border-t border-white/10"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0)' }}
          data-testid="mini-player"
        >
          {/* Progress bar at top */}
          <div className="h-1 bg-zinc-800">
            <div className="h-full bg-white" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
          </div>

          <div className="flex items-center gap-4 p-3 md:p-4">
            {/* Video preview thumbnail — clicking opens fullscreen */}
            <div className="relative w-20 h-14 md:w-24 md:h-16 flex-shrink-0 rounded-lg overflow-hidden bg-zinc-800 cursor-pointer group" onClick={() => setIsFullscreen(true)}>
              {videoUrl ? (
                <video
                  ref={videoRef}
                  poster={getThumbnail(currentVideo)}
                  className="w-full h-full object-cover"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleEnded}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  muted={isMuted}
                  x-webkit-airplay="allow"
                />
              ) : (
                <img src={getThumbnail(currentVideo)} alt={currentVideo.title} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Maximize2 className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium truncate text-sm md:text-base">{currentVideo.title}</h4>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArtistClick(currentVideo);
                  }}
                  className="text-zinc-400 text-xs md:text-sm truncate hover:text-white hover:underline transition-colors"
                  data-testid="mini-player-artist-link"
                >
                  {currentVideo.artist}
                </button>
                {currentVideo.creator_tier && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs"
                    style={{ background: `${currentVideo.creator_tier.color}20`, color: currentVideo.creator_tier.color }}
                    title={currentVideo.creator_tier.name}>
                    {currentVideo.creator_tier.badge}
                  </span>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1 md:gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={toggleMute}
                className={`${isMuted ? 'text-red-400' : 'text-zinc-400 hover:text-white'}`}
                data-testid="mute-btn-mini"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>

              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => handleLikeVideo(currentVideo?.id, !isVideoLiked)}
                className={`${isVideoLiked ? 'text-red-400' : 'text-zinc-400 hover:text-white'}`}
                data-testid="like-btn-mini"
              >
                <Heart className={`w-5 h-5 ${isVideoLiked ? 'fill-red-400' : ''}`} />
              </Button>

              <Button variant="ghost" size="icon" onClick={playPrevious} className="text-zinc-400 hover:text-white hidden sm:flex">
                <SkipBack className="w-5 h-5" />
              </Button>
              
              <Button onClick={togglePlay} className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white text-black hover:bg-zinc-200" data-testid="mini-play-pause">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              
              <Button variant="ghost" size="icon" onClick={playNext} className="text-zinc-400 hover:text-white hidden sm:flex">
                <SkipForward className="w-5 h-5" />
              </Button>

              {/* PiP - visible on all devices */}
              <Button variant="ghost" size="icon" onClick={togglePiP} className={`${isPiP ? 'text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="pip-btn">
                <PictureInPicture2 className="w-5 h-5" />
              </Button>

              {/* AirPlay — Safari/iOS only, auto-hides elsewhere. Same
                  `videoRef` as the mini-player's <video> so the picker
                  hands off the currently-playing element. Gated to
                  non-lyric videos to match the fullscreen player rule. */}
              {!currentVideo?.is_lyric && (
                <AirPlayButton
                  videoRef={videoRef}
                  videoKey={currentVideo?.id}
                  size={18}
                  className="w-9 h-9 text-zinc-400 hover:text-white"
                  data-testid="airplay-btn-mini"
                />
              )}

              <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(true)} className="text-zinc-400 hover:text-white" data-testid="fullscreen-btn">
                <Maximize2 className="w-5 h-5" />
              </Button>

              <Button variant="ghost" size="icon" onClick={closePlayer} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};
